<script setup lang="ts">
import Table from "@/components/Reusables/Table/Table.vue";
import { MaterialServiceLifetimeSelectorParentGroup } from "./types";

defineProps<{
  items: MaterialServiceLifetimeSelectorParentGroup[];
  classificationCodeId?: number;
  materialCategoryId?: string;
  // RTLCA-4737: when true (German/BBSR source), suppress per-child classification
  // code/label badges. Norwegian source keeps the badges (reviewer requirement).
  hideChildClassification?: boolean;
}>();

const emits = defineEmits<{
  (e: "select", lifetime: number): void;
}>();

const handleSelectLifetime = (lifetime: number | null) => {
  if (!lifetime) return;
  emits("select", lifetime);
};
</script>

<template>
  <Table
    class="h-full"
    caption="Material service lifetimes"
    table-class="table-fixed border-separate border-spacing-0 border border-slate-200">
    <template #thead>
      <thead class="text-type-muted sticky top-0 h-[40px] border-b border-slate-300 bg-slate-200">
        <tr class="border-b">
          <td class="t-small-bold text-type-default w-2/3 bg-slate-200 px-4 py-3 text-left tracking-wide">Material / Product</td>
          <td class="t-small-bold text-type-default max-w-[18rem] bg-slate-200 px-3 py-3 text-center tracking-wide">
            Lifetime (years)
          </td>
        </tr>
      </thead>
    </template>

    <template #tbody>
      <tbody
        v-for="parent in items"
        :key="parent.code">
        <!-- Parent section row -->
        <tr class="border-b border-slate-300">
          <td
            class="z-sticky sticky top-[40px] h-[40px] bg-slate-200 px-4 py-2"
            colspan="2">
            <div class="flex items-baseline gap-2 text-xs font-semibold">
              <span class="tracking-wide">{{ parent.code }}</span>
              <span>–</span>
              <span>{{ parent.label }}</span>
            </div>
          </td>
        </tr>

        <template
          v-for="child in parent.children"
          :key="child.code">
          <!--
            Child header row with the classification code/label badge. Only shown
            when (a) there is at least one categorized row in this child group AND
            (b) `hideChildClassification` is false (Norwegian source). For the
            German/BBSR source, the parent group header alone provides the
            context — see RTLCA-4737 reviewer feedback.
          -->
          <tr
            v-if="!hideChildClassification && child.rows.some((r) => !r.isUncategorized)"
            class="border-b border-slate-200 bg-slate-100">
            <td
              class="z-sticky sticky top-[79px] border-t border-slate-200 bg-slate-100 px-4 py-2"
              colspan="2">
              <div class="flex items-center gap-2 px-1 py-1">
                <span
                  class="bg-app-blue-200 rounded-4 text-app-blue-900 inline-flex items-center px-2 py-0.5 text-xs font-semibold">
                  {{ child.code }} - {{ child.label }}
                </span>
              </div>
            </td>
          </tr>

          <!-- Material rows -->
          <tr
            v-for="row in child.rows"
            :key="row.id"
            :class="{
              'bg-app-yellow-50': row.isSuggested,
              'border-b border-slate-200 bg-slate-100': !hideChildClassification && row.isUncategorized && !row.isSuggested,
            }">
            <!-- Material (first column) -->
            <td
              :class="
                !hideChildClassification && row.isUncategorized
                  ? 'border-t border-slate-200 bg-slate-100 px-4 py-2'
                  : 'border-b border-slate-200 px-4 py-2'
              ">
              <div
                :class="
                  !hideChildClassification && row.isUncategorized
                    ? 'flex items-center gap-2 px-1'
                    : 'flex min-w-0 items-center gap-2'
                ">
                <span
                  v-if="row.isSuggested"
                  class="ml-2 inline-flex h-6 w-6 shrink-0 items-center justify-center">
                  <font-awesome-icon
                    class="text-app-yellow-400"
                    :icon="'fa-solid fa-star'" />
                </span>
                <div
                  v-if="hideChildClassification || !row.isUncategorized"
                  class="min-w-0 border-slate-200 pl-3">
                  <span class="block truncate text-xs">{{ row.materialLabel || child.label }}</span>
                </div>
                <!--
                  Inline code/label badge for uncategorized items (rows where the
                  classification code has no associated buildingElement). Hidden
                  for the German/BBSR source — see RTLCA-4737 reviewer feedback.
                -->
                <span
                  v-else
                  class="bg-app-blue-200 rounded-4 text-app-blue-900 inline-flex items-center px-2 py-0.5 text-xs font-semibold">
                  {{ child.code }} - {{ child.label }}
                </span>
              </div>
            </td>

            <!-- Lifetime -->
            <td
              :class="
                !hideChildClassification && row.isUncategorized
                  ? 'max-w-[18rem] overflow-hidden border-t border-slate-200 bg-slate-100 px-3 py-2'
                  : 'max-w-[18rem] overflow-hidden border-b border-slate-200 px-3 py-2'
              ">
              <div class="grid w-full grid-cols-3 gap-1">
                <template v-if="row.avg != null && row.low == null && row.high == null">
                  <button
                    v-tooltip="{ text: `Select average lifetime (${row.avg} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 duration-fast ease-standard bg-slate-0 col-span-3 flex flex-col items-center border border-slate-300 px-3 py-1 shadow-xs transition-colors"
                    @click="handleSelectLifetime(row.avg)">
                    <span class="text-type-muted text-[9px] font-medium tracking-wide uppercase">Avg</span>
                    <span class="text-type-default text-sm font-semibold">{{ row.avg }}</span>
                  </button>
                </template>
                <template v-else>
                  <button
                    v-if="row.low != null"
                    v-tooltip="{ text: `Select low lifetime (${row.low} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 duration-fast ease-standard bg-slate-0 flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    @click="handleSelectLifetime(row.low)">
                    <span class="text-type-muted text-[9px] font-medium tracking-wide uppercase">↓ Low</span>
                    <span class="text-type-default text-sm font-semibold">{{ row.low }}</span>
                  </button>
                  <button
                    v-if="row.avg != null"
                    v-tooltip="{ text: `Select average lifetime (${row.avg} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 duration-fast ease-standard bg-slate-0 flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    @click="handleSelectLifetime(row.avg)">
                    <span class="text-type-muted text-[9px] font-medium tracking-wide uppercase">Avg</span>
                    <span class="text-type-default text-sm font-semibold">{{ row.avg }}</span>
                  </button>
                  <button
                    v-if="row.high != null"
                    v-tooltip="{ text: `Select high lifetime (${row.high} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 duration-fast ease-standard bg-slate-0 flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    @click="handleSelectLifetime(row.high)">
                    <span class="text-type-muted text-[9px] font-medium tracking-wide uppercase">↑ High</span>
                    <span class="text-type-default text-sm font-semibold">{{ row.high }}</span>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </template>
  </Table>
</template>
